import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Share2, Check, ArrowUpRight } from 'lucide-react';
import { SPOTIFY_SHOW_URL } from '../data/season1Episodes';
const APPLE_PODCASTS_URL = 'https://podcasts.apple.com/us/podcast/3-doves-podcast/id1871795398';
const SHARE_TEXT = 'Real stories of faith from the 3 Doves Podcast — give it a listen!';
export function HelpUsReach() {
  const [shareState, setShareState] = useState<'idle' | 'copied' | 'error'>('idle');
  const handleShare = async () => {
    const url = window.location.origin;
    try {
      if (navigator.share) {
        await navigator.share({
          title: '3 Doves Podcast',
          text: SHARE_TEXT,
          url
        });
        return;
      }
      await navigator.clipboard.writeText(`${SHARE_TEXT} ${url}`);
      setShareState('copied');
      window.setTimeout(() => setShareState('idle'), 2500);
    } catch (err) {
      if (err instanceof DOMException && err.name === 'AbortError') return;
      setShareState('error');
      window.setTimeout(() => setShareState('idle'), 2500);
    }
  };
  const actions = [{
    verb: 'Like & follow',
    where: 'on Spotify',
    href: SPOTIFY_SHOW_URL,
    cta: 'Open Spotify'
  }, {
    verb: 'Follow & rate',
    where: 'on Apple Podcasts (iTunes)',
    href: APPLE_PODCASTS_URL,
    cta: 'Open Apple Podcasts'
  }];
  return <section aria-labelledby="help-reach-heading" className="py-20 md:py-28 bg-dark-700 border-t border-dark-600" data-id="element-86" data-mp="379f57pwpbork">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center" data-id="element-87" data-mp="3c7yxe81w1wsd">
        {/* Message */}
        <motion.div initial={{
        opacity: 0,
        y: 16
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.3,
        ease: [0.23, 1, 0.32, 1]
      }} className="lg:col-span-6" data-id="element-88" data-mp="3m6fr3cs7opi4">
          <h2 id="help-reach-heading" className="text-3xl md:text-5xl font-serif font-bold text-white leading-tight mb-6" data-id="element-89" data-mp="11hs0y71g5opm">
            Here is how you can help 3 Doves{' '}
            <span className="text-gold-500" data-id="element-90" data-mp="2sm3ali28lrto">reach millions.</span>
          </h2>
          <p className="text-4xl md:text-6xl font-serif font-bold text-white mb-8" data-id="element-91" data-mp="36ip5qlapx2ke">
            Share. Like. Follow.
          </p>
          <p className="text-lg text-white/80 leading-relaxed max-w-xl" data-id="element-92" data-mp="2esc30f82gi0l">
            Every share and every like on Spotify and iTunes helps more people
            find the podcast. And the more people who listen, the more we get to
            share how God used your stories to reach the masses with the good
            news of Jesus Christ.
          </p>
        </motion.div>

        {/* Actions */}
        <motion.ol initial={{
        opacity: 0,
        y: 16
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.3,
        delay: 0.08,
        ease: [0.23, 1, 0.32, 1]
      }} className="lg:col-span-6 divide-y divide-dark-600 border-y border-dark-600" data-id="element-93" data-mp="1v43a22yp8801">
          <li className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 py-6" data-id="element-94" data-mp="1lprppoo2aqat">
            <div data-id="element-95" data-mp="17158tiisdqhh">
              <p className="text-2xl font-serif font-bold text-white" data-id="element-96" data-mp="171oloktkxoon">
                Share
              </p>
              <p className="text-white/60" data-id="element-97" data-mp="1c085aekpaodu">
                Send the podcast to a friend who needs hope today.
              </p>
            </div>
            <button type="button" onClick={handleShare} className="inline-flex items-center justify-center gap-2 shrink-0 whitespace-nowrap px-5 py-3 rounded-md bg-gold-500 text-dark-900 font-semibold hover:bg-gold-400 transition-colors duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-gold-400 focus-visible:ring-offset-2 focus-visible:ring-offset-dark-700" aria-live="polite" data-id="element-98" data-mp="24odubdisezjh">
              {shareState === 'copied' ? <>
                  <Check className="w-4 h-4" data-id="element-99" data-mp="zj8pzkf5yvxk" /> Link copied
                </> : shareState === 'error' ? 'Try again' : <>
                  <Share2 className="w-4 h-4" data-id="element-100" data-mp="prksb1s0kinn" /> Share 3 Doves
                </>}
            </button>
          </li>
          {actions.map(action => <li key={action.href} className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 py-6" data-id="element-101" data-mp="1ih1n2633wbn1">
              <div data-id="element-102" data-mp="1dihw1r9no9nt">
                <p className="text-2xl font-serif font-bold text-white" data-id="element-103" data-mp="1dfvt64xpmibx">
                  {action.verb}
                </p>
                <p className="text-white/60" data-id="element-104" data-mp="13k10si3op8vy">{action.where}</p>
              </div>
              <a href={action.href} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 shrink-0 whitespace-nowrap px-5 py-3 rounded-md border border-gold-500 text-gold-400 font-semibold hover:bg-gold-500 hover:text-dark-900 transition-colors duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-gold-400 focus-visible:ring-offset-2 focus-visible:ring-offset-dark-700" data-id="element-105" data-mp="13m57f7sywlzx">
                {action.cta} <ArrowUpRight className="w-4 h-4" data-id="element-106" data-mp="2ihdc2u2ram0j" />
              </a>
            </li>)}
        </motion.ol>
      </div>
    </section>;
}